import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import {
  ExpeditionDocumentFileDto,
  GetOneExpeditionBoResponseDto,
  GetOneExpeditionResponseDto,
} from "@/types/api/api-types";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { useUpdateFileBo, useDeleteFileByIdBo, useDeleteFileById } from "@/api/file.api";
import { ExpeditionDocumentCard } from "@/components/backOffice/expeditionForm/expeditionDocumentCard";
import {
  UploadFileBoModal,
  UploadFileClientModal,
} from "@/components/backOffice/expeditionForm/uploadFileModal";
import { ContextRules } from "@/constants/fileCrud";
import { useFileTypeLabels } from "@/hooks/useEnumLabels";

type DocumentsUpdateModalProps = {
  open: boolean;
  onClose: () => void;
  expeditionId: string;
  documents: ExpeditionDocumentFileDto[];
  expeditionDocumentsId?: string;
  invalidateQueryKey: unknown[];
  title?: string;
  isBo?: boolean;
  expedition?: GetOneExpeditionResponseDto | GetOneExpeditionBoResponseDto;
  fileContextRules: ContextRules;
  isLoading?: boolean;
};

export const DocumentsUpdateModal = ({
  open,
  onClose,
  expeditionId,
  documents,
  expeditionDocumentsId,
  invalidateQueryKey,
  title,
  isBo = false,
  expedition,
  fileContextRules,
  isLoading = false,
}: DocumentsUpdateModalProps) => {
  const { t } = useTranslation(["shared", "common"]);
  const fileTypeLabels = useFileTypeLabels();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { mutateAsync: updateFile } = useUpdateFileBo();
  const { mutateAsync: deleteFileBo } = useDeleteFileByIdBo();
  const { mutateAsync: deleteFile } = useDeleteFileById();

  const addableFileTypes = fileContextRules
    ? Object.entries(fileContextRules)
        .filter(([, crud]) => crud.canCreate)
        .map(([type]) => type as FileTypeEnum)
    : undefined;

  const canAddFiles = !isLoading && addableFileTypes && addableFileTypes.length > 0;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
  };

  const handleToggleSendViaApi = async (fileId: string, value: boolean) => {
    await updateFile({ id: fileId, payload: { sendViaApi: value } });
    invalidate();
  };

  const handleToggleIsPrivate = async (fileId: string, value: boolean) => {
    await updateFile({ id: fileId, payload: { isPrivate: value } });
    invalidate();
  };

  const handleDelete = async (fileId: string) => {
    if (isBo) {
      await deleteFileBo(fileId);
    } else {
      await deleteFile(fileId);
    }
    invalidate();
    toast.success(t("documentsUpdateModal.deleteSuccess"));
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={title ?? t("documentsUpdateModal.title")}
        size="md"
        dismissible
        contentClassName="max-w-[600px]"
        footer={
          <div className="flex w-full flex-row justify-between gap-2">
            {canAddFiles && (
              <Button variant="outline" type="button" onClick={() => setUploadModalOpen(true)}>
                <Plus className="mr-2 size-4" />
                {t("documentsUpdateModal.addFile")}
              </Button>
            )}
            <Button variant="default" onClick={onClose} className="ml-auto">
              {t("common:actions.close")}
            </Button>
          </div>
        }
      >
        <div className="flex max-h-[70vh] w-full flex-col gap-3 overflow-y-auto pr-1">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
            </div>
          )}
          {!isLoading && documents.length === 0 && (
            <p className="py-4 text-center text-sm text-gray-500">
              {t("documentsUpdateModal.empty")}
            </p>
          )}
          {!isLoading &&
            documents.map((file) => {
              const perm = fileContextRules[file.type as FileTypeEnum];
              const typeLabel = fileTypeLabels[file.type as FileTypeEnum] ?? file.type;
              return (
                <div key={file.id} className="relative">
                  <ExpeditionDocumentCard
                    file={file}
                    onToggleSendViaApi={
                      perm.canToggleSendViaApi ? handleToggleSendViaApi : undefined
                    }
                    onToggleIsPrivate={perm.canTogglePrivate ? handleToggleIsPrivate : undefined}
                    onDelete={perm.canDelete ? handleDelete : undefined}
                    expedition={expedition as GetOneExpeditionBoResponseDto}
                    hideDetailsInfo={true}
                  />
                  {isBo && (
                    <Badge
                      variant="lightGray"
                      className="absolute bottom-0 left-12 px-1.5 py-0 text-[12px]"
                    >
                      {typeLabel}
                    </Badge>
                  )}
                </div>
              );
            })}
        </div>
      </Modal>

      {canAddFiles &&
        (isBo ? (
          <UploadFileBoModal
            expedition={expedition as GetOneExpeditionBoResponseDto}
            open={uploadModalOpen}
            onOpenChange={setUploadModalOpen}
            expeditionDocumentsId={expeditionDocumentsId}
            expeditionId={expeditionId}
            allowedFileTypes={addableFileTypes}
            invalidateQueryKey={invalidateQueryKey}
            disabledOptions={{
              isPrivate: false,
              sendViaApi: false,
            }}
          />
        ) : (
          <UploadFileClientModal
            open={uploadModalOpen}
            onOpenChange={setUploadModalOpen}
            expeditionDocumentsId={expeditionDocumentsId}
            expeditionId={expeditionId}
            allowedFileTypes={addableFileTypes}
            invalidateQueryKey={invalidateQueryKey}
            submitOnDrop={true}
          />
        ))}
    </>
  );
};
